// Die Claude-API im Homelab (internal/ki, docs/ki.md): das monatliche
// API-Guthaben (Kostenbuch mit harter Grenze) und die Repo-Prüfung über das
// Agent SDK. Getrennt vom Abo, über das die Chats laufen.

export interface KiLauf {
  seit: string;
  nummer: number;
  von: number;
  titel: string;
  schritte: number;
  letzte: string;
  reserviert_usd: number;
}

export interface KiBericht {
  datei: string;
  name: string;
  titel: string;
  repo: string;
  commit: string;
  modell: string;
  beginn: string;
  ende: string;
  /** fertig | budget (Grenze riss, Bericht aus dem Stand) | schritte | fehler */
  ausgang: string;
  fehler?: string;
  /** z. B. Opus 5.5 lehnte ab, Opus 5 antwortete */
  hinweis?: string;
  schritte: number;
  kosten_usd: number;
  befunde: number;
  schwere: Record<string, number>;
}

export interface KiStand {
  schluessel: boolean;
  monat_usd: number;
  grenze_usd: number;
  gilt_bis: string;
  zwecke: { zweck: string; anzahl: number; kosten_usd: number }[];
  lauf: KiLauf | null;
  /** warum der letzte Start scheiterte (kein Schlüssel, Grenze, Image)
   *  oder der Container abbrach; leer nach einem gelungenen Start */
  fehler?: string;
  berichte: KiBericht[];
  /** Laut Konto bei Anthropic (internal/ki/konto.go); monat_usd oben ist
   *  das eigene Kostenbuch (geschätzt, ohne Abgebrochenes). */
  /** Ausgaben des Monats, so genau wie möglich, und woher */
  ausgaben_usd: number;
  ausgaben_quelle: "konto" | "anker" | "buch";
  guthaben_quelle?: "konto" | "buch";
  admin_schluessel: boolean;
  /** ein Schlüssel liegt im falschen Fach (am Präfix erkannt) */
  schluessel_hinweis?: string;
  konto_monat_usd?: number;
  konto_abgerufen?: string;
  konto_fehler?: string;
  /** Guthaben jetzt = Anker aus der Console − Kosten seither */
  guthaben_usd?: number;
  guthaben_am?: string;
  guthaben_anker_usd?: number;
}

/** Ausgaben des Monats und woher: laut Konto (Admin-Schlüssel), gerechnet
 *  (Wert aus der Console + Kostenbuch seither) oder geschätzt (nur das
 *  Kostenbuch). */
export function monatAusgaben(s: KiStand | undefined): { usd: number; quelle: "konto" | "anker" | "buch" } {
  return { usd: s?.ausgaben_usd ?? s?.monat_usd ?? 0, quelle: s?.ausgaben_quelle ?? "buch" };
}

/** Die Aufgabe, die die Repo-Prüfung startet (Migration 158). */
export const PRUEF_AUFGABE = "ki-repo-pruefung";

const USD = new Intl.NumberFormat("de-DE", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const USD_GANZ = new Intl.NumberFormat("de-DE", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/** 12,40 $ */
export function usd(x: number | undefined): string {
  return USD.format(x ?? 0);
}

/** 95 $ (Grenzen sind ganze Beträge) */
export function usdGanz(x: number | undefined): string {
  return Number.isInteger(x ?? 0) ? USD_GANZ.format(x ?? 0) : USD.format(x ?? 0);
}

/** Anteil der Grenze, der ausgegeben ist (0..1). */
export function kiAnteil(s: KiStand | undefined): number {
  if (!s || s.grenze_usd <= 0) return 0;
  return monatAusgaben(s).usd / s.grenze_usd;
}

/** Tage bis zum Ende des Guthabens (ganze Tage, ab heute 0 Uhr). */
export function tageBis(datum: string, jetzt: Date): number {
  const [j, m, t] = datum.split("-").map(Number);
  if (!j || !m || !t) return Infinity;
  const ende = new Date(j, m - 1, t);
  const heute = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  return Math.round((ende.getTime() - heute.getTime()) / 86_400_000);
}

/** 15.11. */
export function tagKurz(datum: string): string {
  const [, m, t] = datum.split("-");
  return m && t ? `${Number(t)}.${Number(m)}.` : datum;
}

/** Je Prüfung nur der neueste Bericht (Tim 21.10.2026: „Hier braucht es nur
 *  die aktuellen Ergebnisse"). Die Liste kommt neueste zuerst. */
export function aktuelleBerichte(berichte: KiBericht[] | undefined): KiBericht[] {
  const gesehen = new Set<string>();
  return (berichte ?? []).filter((b) => {
    if (gesehen.has(b.name)) return false;
    gesehen.add(b.name);
    return true;
  });
}

/** Die früheren Berichte derselben Prüfung, neueste zuerst. */
export function fruehereBerichte(berichte: KiBericht[] | undefined, b: KiBericht | undefined): KiBericht[] {
  if (!b) return [];
  return (berichte ?? []).filter((x) => x.name === b.name && x.datei < b.datei);
}

const SCHWERE = ["kritisch", "hoch", "mittel", "niedrig"] as const;

/** „2 kritisch · 3 hoch · 5 weitere" -- nur was vorkommt. */
export function schwereText(b: KiBericht): string {
  const teile: string[] = [];
  let rest = b.befunde;
  for (const s of SCHWERE.slice(0, 2)) {
    const n = b.schwere?.[s] ?? 0;
    if (n) teile.push(`${n} ${s}`);
    rest -= n;
  }
  if (rest > 0) teile.push(teile.length ? `${rest} weitere` : `${rest}`);
  return teile.join(" · ");
}

/** Ton eines Berichts: kritisch = rot, hoch = gelb, sonst ruhig. */
export function berichtTon(b: KiBericht): "danger" | "warn" | undefined {
  if (b.ausgang === "fehler") return "danger";
  if (b.schwere?.kritisch) return "danger";
  if (b.schwere?.hoch) return "warn";
  return undefined;
}
